/* iter55f73 — Admin tab groups.
 * iter-scope-visibility — Adds TAB_SCOPES + SCOPE_TAB_ORDER so admins
 * whose account is restricted to `financials`, `coach`, or `editor`
 * see a purposeful subset of the sidebar on login instead of the
 * grand-fathered full-console default (`pro_writer`).
 *
 * Splits the (very large) admin console into three concerns:
 *
 *   • finance     — anything that moves money or measures margin
 *   • users       — anything about a human account or people-services
 *   • operations  — anything about system config, infrastructure, or
 *                    the marketing/help/QA surface
 *
 * `profile` and `admin-guide` are cross-cutting and appear on every
 * group so admins never lose access to their own profile or the help
 * manual regardless of which page they landed on.
 *
 * The grouping is read by AdminPage.jsx which uses it to decide which
 * `TabsTrigger`s to render and which tab to default to.
 */

export const ADMIN_TAB_GROUPS = {
  // Feb-2026 · Top-tier Admin surface — cross-panel oversight only.
  // Ledger / Balance Sheet / Income are duplicated as top-level tabs
  // inside Finance too so a CFO doesn't have to leave their workspace,
  // but the "Admin" group is the single pane where a full Admin lands
  // to see red flags across every panel plus closed issues.
  admin: [
    'admin-overview',        // Cross-panel red-flag summary + closed issues
    'editorial-review',      // Hybrid Loom manuscript risk inbox
    'ledger',                // Full Ledger + COA access from Admin
    'balance-sheet', 'income-statement', 'cash-flow', 'equity-statement',
    'compliance', 'ap-approvals', 'stripe-payments', 'aging',
    'revenue-features',      // Zernio publishing kill switch + revenue controls
    'permissions',           // Grant / revoke scopes
    'admin-users',           // Create / delete admin accounts
    'security',              // Abuse-prevention console
    'admin-inbox',
  ],
  finance: [
    'admin-overview',        // Also reachable from Finance for CFOs
    'bookkeeping',           // Sep-2026 · Focused ledger-clerk surface
    'ledger',                // Feb-2026 · Top-level Ledger + COA
    'balance-sheet',         // Feb-2026 · Top-level Balance Sheet
    'income-statement',      // Feb-2026 · Top-level Income Statement
    'cash-flow',             // Feb-2026 · Top-level Cash Flow
    'equity-statement',      // Feb-2026 · Top-level Statement of Equity
    'pricing', 'budget', 'plans', 'accounting', 'financials',
    'unit-econ', 'royalties', 'marketing-bookings', 'tax-center',
    'compliance',
    'aging',
    'bank-recon',
    'auto-ledger',
    'ap-approvals',
    'stripe-payments',
    'inventory',
    'revenue-features',
    'referrals',             // Feb-2026 — Referral program dashboard
    'free-tier',             // Feb-2026 — Free tier closure (exclusivity)
    'canva-dashboard',       // Feb-2026 — Canva cover order history + revenue chart
    'admin-inbox',
  ],
  users: [
    'users', 'archived-users', 'teams', 'roles', 'memberships',
    'editorial-review', 'human-services', 'editing', 'admin-users',
    'permissions',   // Feb-2026 · Dedicated Permissions matrix
    'demo-funnel',
    'security',      // iter-p0-security — abuse-prevention console
    'admin-inbox',   // iter-inbox-pin — Administrator mailbox on every surface
  ],
  operations: [
    'models', 'retro-review', 'continuity-review', 'collections', 'seo', 'blog', 'landing',
    'what-we-offer',       // 2026-02 — WhatWeOffer feature cards CMS
    'starter-templates', 'genre-variants', 'clarity', 'analytics', 'isbn', 'audiobook-voices', 'elevenlabs-narration', 'help-manual',
    'tips',            // iter-tips-editor — HelpTip popover CRUD
    'training', 'teleplay-qa', 'seo-config', 'feature-videos', 'audit', 'capacity', 'qa',
    'ohas',
    'wipo-contracts',
    'author-agreements',   // Feb-2026 — Publishing Agreement countersignature queue
    'isbn-bank',           // Feb-2026 — ISBN Bank pool + assignment editor
    'eco-filing',
    'indie-reader',   // iter55z-audit round 9 — companion-mode surface
    'editorial-review', // Sep-2026 — completed Loom risk review inbox
    'reader-review',  // Feb-2026 — editor review queue
    'editor-workbench', // Sep-2026 — full Publishing Editor Workbench embedded in Admin
    'editor-seats',    // Feb-2026 — editor seat management
    'companion-inbox', // Feb-2026 — parent ↔ Companion mailbox
    'email-workers',   // iter55z-audit round 9 (extras 2) — wrangler CLI generator
    'request-log',     // Feb-2026 — build/feature request export log
    'writer-feedback', // iter-feedback — writer suggestion inbox
    'comparison-pages', // Feb-2026 — AEO comparison landing pages
    'email-log',       // Feb-2026 — outbound email send log (pinned tab)
    'admin-inbox',   // iter-inbox-pin — Administrator mailbox on every surface
    'ai-caching',    // Prompt caching config (platform-wide task toggles)
  ],
  editing: [
    // ── Core editorial pipeline ──────────────────────────────────────────
    'editorial-review',      // Manuscript Review (Hybrid Loom risk inbox)
    'reader-review',         // Editor review queue
    'editor-workbench',      // Publishing Editor Workbench
    'editing',               // Editing tools panel
    'ms-export',             // Bulk ZIP export of manuscripts + metadata
    'bulk-novel-eval',       // Admin Bulk Novel Evaluation & Rewrite
    'plot-builder',          // Plot Extraction + Sales Readiness builder
    'editor-seats',          // Editor seat management
    'retro-review',          // Retroactive prose review
    'continuity-review',     // Continuity & Drift walk
    // ── Manuscript & prose tools ────────────────────────────────────────
    'writer-feedback',       // Written Feedback inbox
    'genre-variants',        // Genre Variants CMS
    'teleplay-qa',           // TelePlay QA
    'indie-reader',          // Indie Reader companion surface
    'companion-inbox',       // Parent ↔ Companion mailbox
    // ── Publishing & IP ─────────────────────────────────────────────────
    'author-agreements',     // Publishing Agreement countersignature
    'wipo-contracts',        // WIPO Contracts
    'isbn',                  // ISBN Manager
    'isbn-bank',             // ISBN Bank pool
    'eco-filing',            // eCO Filing
    'audiobook-voices',      // Audiobook production voices
    // ── Project & team management ────────────────────────────────────────
    'teams',                 // Teams & Projects
    'memberships',           // Project Members
    // ── Mailbox ─────────────────────────────────────────────────────────
    'admin-inbox',
  ],
  marketing: [
    'media-development',
    'media-vault',
    'marketing-venues',
  ],
  // Feb-2026 iter — dedicated IT Management surface. Only admins with
  // the `it_management` (or `pro_writer`) scope can see this group.
  it: [
    'it-management',
    'keys',          // API Keys moved from Operations — Feb-2026 restructure
    'vault',         // Secrets Vault moved from Operations
    'admin-inbox',
  ],
  // Indie Reader Admin — dedicated companion app surface (Feb-2026)
  indie_reader: [
    'indie-reader',       // Diagnostics · books · chapters · covers
    'companion-inbox',    // Parent ↔ Companion mailbox
    'reader-sync',        // Catalog sync health check
    'admin-inbox',
  ],
};

// Tabs available regardless of group. iter-inbox-everywhere — the admin
// mailbox is now always in scope so every restricted admin can reach the
// messages sitting under their permission set without hopping surfaces.
export const ALWAYS_ON_TABS = ['admin-inbox', 'profile', 'admin-guide'];

export const GROUP_META = {
  admin:      { label: 'Admin',             path: '/admin/admin',      tone: 'Executive oversight — cross-panel red flags, closed issues, permissions, security.' },
  finance:    { label: 'Finance',           path: '/admin/finance',    tone: 'Money in, money out — Ledger, Balance Sheet, Income, Cash Flow, pricing, budgets.' },
  users:      { label: 'Users',             path: '/admin/users',      tone: 'People and their services — accounts, teams, editorial bookings.' },
  operations: { label: 'Operations',        path: '/admin/operations', tone: 'System config — models, keys, capacity, marketing, QA.' },
  editing:    { label: 'Editing & Books',   path: '/admin/editing',       tone: 'Editorial pipeline — manuscript review, approval, workbench, drift correction, publishing workflows.' },
  marketing:  { label: 'Marketing & Media', path: '/admin/marketing',     tone: 'Training reels, campaign media, and production status.' },
  it:         { label: 'IT',                path: '/admin/it',            tone: 'Network, secrets, API keys, cron & log oversight — scope-isolated from other admins.' },
  indie_reader: { label: 'Indie Reader Admin', path: '/admin/indie-reader', tone: 'Companion app management — diagnostics, book sync, chapters, cover upload, inbox, and catalog health.' },
};

export function tabsForGroup(group) {
  const primary = ADMIN_TAB_GROUPS[group] || [];
  return new Set([...primary, ...ALWAYS_ON_TABS]);
}

export function defaultTabForGroup(group) {
  return (ADMIN_TAB_GROUPS[group] || [])[0] || 'profile';
}

/* --------------------------------------------------------------
 * iter-scope-visibility — Per-scope tab visibility & ordering.
 *
 * TAB_SCOPES maps every admin tab-value to the list of scopes that
 * may see it. `pro_writer` is included on every restricted tab so
 * the grand-fathered full-console admins keep their existing view.
 *
 * SCOPE_TAB_ORDER dictates the sidebar order shown to a limited-
 * scope admin: the first scope in the operator's `scopes` list that
 * has a defined order wins. This lets a `financials` admin land on
 * "Unit Economics" first and see the money tabs stacked at the top
 * instead of alphabetically buried mid-sidebar.
 * ---------------------------------------------------------------*/
export const TAB_SCOPES = {
  // ── IT Management (isolated from every other admin function) ────
  // Feb-2026 iter — `viewer` (CEO) also unlocks the IT tab because
  // every read endpoint on the panel accepts viewer. Mutating actions
  // still require `it_management` (or `pro_writer`) server-side.
  'it-management':        ['it_management', 'viewer', 'pro_writer'],

  // ── Dev mailbox (dev scope + pro_writer) ───────────────────────
  // 'dev-inbox' removed — no TabsTrigger/TabsContent wired in AdminPage; orphan entry

  // ── Finance (financials scope + pro_writer, viewer read-only) ─
  'pricing':              ['financials', 'pro_writer'],
  'budget':               ['financials', 'pro_writer'],
  'plans':                ['financials', 'pro_writer'],
  'accounting':           ['financials', 'pro_writer'],
  'author-royalty-tiers': ['financials', 'pro_writer'],
  'royalties':            ['financials', 'pro_writer'],
  'marketing-bookings':   ['financials', 'pro_writer'],
  'tax-center':           ['financials', 'pro_writer'],
  'revenue-features':     ['financials', 'pro_writer'],  // Feb-2026 revenue lifts

  // ── Feb-2026 · Compliance & Licenses ──────────────────────────
  'compliance':           ['compliance', 'financials', 'ap_approver', 'pro_writer'],

  // ── Feb-2026 · AR/AP aging + bank reconciliation ─────────────
  'aging':                ['financials', 'ap_ar', 'ap_approver', 'viewer', 'pro_writer'],
  'bank-recon':           ['financials', 'ap_ar', 'ap_approver', 'pro_writer'],
  'auto-ledger':          ['financials', 'ap_approver', 'viewer', 'pro_writer'],
  'ap-approvals':         ['financials', 'ap_approver', 'pro_writer'],
  'stripe-payments':      ['financials', 'viewer', 'pro_writer'],
  'inventory':            ['financials', 'ap_ar', 'ap_approver', 'viewer', 'pro_writer'],

  // ── Feb-2026 · Top-level SEC-standard statements ─────────────
  'ledger':               ['financials', 'ledger_entry', 'ap_approver', 'viewer', 'pro_writer'],
  'bookkeeping':          ['ledger_entry', 'pro_writer'],  // Sep-2026 — focused Ledger clerk panel
  'balance-sheet':        ['financials', 'viewer', 'pro_writer'],
  'income-statement':     ['financials', 'viewer', 'pro_writer'],
  'cash-flow':            ['financials', 'viewer', 'pro_writer'],
  'equity-statement':     ['financials', 'viewer', 'pro_writer'],

  // ── Feb-2026 · Admin Overview (pro_writer-only summary) ────────
  'admin-overview':       ['pro_writer'],

  // ── Feb-2026 · Dedicated Permissions matrix (pro_writer-only) ─
  'permissions':          ['pro_writer'],

  // ── Feb-2026 · Fine-grained finance permissions ───────────────
  // `ledger_entry` sees only the Financial Statements panel where
  // the Ledger & COA tab lets them post journal entries.
  // `ap_ar` gets ledger + accounting + AP approval queue as viewer.
  // `ap_approver` gets the full AP approval queue with approve/reject.
  //
  // Note: the actual tab shown to these scopes is `financials`; the
  // permission-gating for what they can *do* inside that panel is
  // enforced server-side via require_scope on each POST endpoint.

  // ── Editorial services (coach / editor / pro_writer) ───────────
  'human-services':       ['coach', 'editor', 'pro_writer'],
  'editorial-review':     ['editor', 'pro_writer'],
  'reader-review':        ['editor', 'pro_writer'],
  'editor-workbench':     ['editor', 'pro_writer'],
  'editor-seats':         ['editor', 'pro_writer'],
  'editing':              ['editor', 'pro_writer'],
  'plot-builder':         ['editor', 'pro_writer'],

  // ── Cross-cutting mailboxes ────────────────────────────────────
  'admin-inbox':          ['financials', 'coach', 'editor', 'viewer', 'pro_writer'],
  // CEO viewer also gets read-only access to these exec dashboards.
  'unit-econ':            ['financials', 'viewer', 'pro_writer'],
  'financials':           ['financials', 'viewer', 'pro_writer', 'ledger_entry', 'ap_ar', 'ap_approver'],
  'accounting':           ['financials', 'pro_writer', 'ap_ar', 'ap_approver'],
  'analytics':            ['viewer', 'pro_writer'],

  // ── Finance extras (financials scope) ────────────────────────
  'referrals':            ['financials', 'pro_writer'],
  'free-tier':            ['financials', 'pro_writer'],
  'canva-dashboard':      ['financials', 'pro_writer'],

  // ── Operations extras (pro_writer-only for now) ───────────────
  'blog':                 ['pro_writer'],
  'comparison-pages':     ['pro_writer'],
  'ai-caching':           ['pro_writer'],

  // ── Editing extras ────────────────────────────────────────────
  'bulk-novel-eval':      ['editor', 'pro_writer'],
  'ms-export':            ['pro_writer'],
  'writer-feedback':      ['editor', 'pro_writer'],

  // ── Every remaining tab is pro_writer-only for now ─────────────
  'users':                ['pro_writer'],
  'archived-users':       ['pro_writer'],
  'teams':                ['pro_writer'],
  'roles':                ['pro_writer'],
  'memberships':          ['pro_writer'],
  'models':               ['pro_writer'],
  'retro-review':         ['pro_writer'],
  'continuity-review':    ['pro_writer'],
  'keys':                 ['pro_writer'],
  'vault':                ['pro_writer'],
  'collections':          ['pro_writer'],
  'seo':                  ['pro_writer'],
  'landing':              ['pro_writer'],
  'what-we-offer':        ['pro_writer'],  // 2026-02 — WhatWeOffer CMS
  'starter-templates':    ['pro_writer'],
  'genre-variants':       ['pro_writer'],
  'author-rankings':      ['pro_writer'],
  'clarity':              ['pro_writer'],
  'isbn':                 ['pro_writer'],
  'audiobook-voices':     ['pro_writer'],
  'ohas':                 ['pro_writer'],
  'wipo-contracts':       ['pro_writer'],
  'author-agreements':    ['pro_writer'],
  'isbn-bank':            ['pro_writer'],
  'eco-filing':           ['pro_writer'],
  'admin-users':          ['pro_writer'],
  'broadcasts':           ['pro_writer'],
  'help-manual':          ['pro_writer'],
  'tips':                 ['pro_writer'],
  'training':             ['pro_writer'],
  'teleplay-qa':          ['pro_writer'],
  'seo-config':           ['pro_writer'],
  'feature-videos':       ['pro_writer'],
  'demo-funnel':          ['pro_writer'],
  'audit':                ['pro_writer'],
  'security':             ['pro_writer'],
  'capacity':             ['pro_writer'],
  'indie-reader':         ['pro_writer'],
  'companion-inbox':      ['pro_writer'],
  'email-workers':        ['pro_writer'],
  'request-log':          ['pro_writer'],
  'email-log':            ['pro_writer'],
  'qa':                   ['pro_writer'],
  'media-development':    ['marketing_media', 'pro_writer'],
  'media-vault':          ['marketing_media', 'pro_writer'],
  'marketing-venues':     ['marketing_media', 'pro_writer'],
};

export const SCOPE_TAB_ORDER = {
  // Feb-2026 iter — CEO's read-only viewer sees the exec-visible
  // surfaces: IT stress, unit economics, and their own mailbox.
  viewer: [
    'it-management', 'unit-econ', 'financials', 'analytics',
    'admin-inbox', 'profile', 'admin-guide',
  ],
  it_management: [
    'it-management', 'admin-inbox', 'profile', 'admin-guide',
  ],
  dev: [
    'keys', 'models', 'admin-inbox', 'profile', 'admin-guide',
  ],
  financials: [
    'unit-econ', 'financials', 'plans', 'pricing', 'budget',
    'accounting', 'royalties', 'author-royalty-tiers',
    'marketing-bookings', 'tax-center', 'compliance',
    'admin-inbox',
    'profile', 'admin-guide',
  ],
  coach: [
    'human-services', 'admin-inbox', 'profile', 'admin-guide',
  ],
  editor: [
    'editor-workbench', 'editorial-review', 'reader-review', 'human-services', 'editing', 'editor-seats',
    'admin-inbox', 'profile', 'admin-guide',
  ],
  marketing_media: [
    'media-development', 'media-vault', 'marketing-venues', 'admin-inbox', 'profile', 'admin-guide',
  ],
  // Feb-2026 · Fine-grained finance scopes each land on the Financial
  // Statements panel — that surface hosts the Ledger, AP Approvals, and
  // the accounting hub they'll actually work in.
  // Sep-2026 · `ledger_entry` now lands on a focused Bookkeeping panel
  // (Chart of Accounts + manual entries only) instead of the full
  // Financial Statements suite.
  ledger_entry: [
    'bookkeeping', 'admin-inbox', 'profile', 'admin-guide',
  ],
  ap_ar: [
    'financials', 'accounting', 'admin-inbox', 'profile', 'admin-guide',
  ],
  ap_approver: [
    'financials', 'accounting', 'compliance', 'admin-inbox', 'profile', 'admin-guide',
  ],
};

/**
 * iter-scope-visibility — Given the current admin's scopes, return
 *   • `visible`: Set of tab values that should render (or `null`
 *      when the admin has `pro_writer` — meaning "no restriction,
 *      keep the legacy full-console").
 *   • `order`:   Ordered array of tabs from SCOPE_TAB_ORDER, filtered
 *      to only-visible tabs. `null` when unrestricted.
 *   • `primary`: The scope that dictated the order (for UI headings).
 */
export function tabsForScopes(scopes) {
  const list = Array.isArray(scopes) ? scopes : [];
  // `pro_writer` = grand-fathered full-console; no filtering.
  if (list.includes('pro_writer')) {
    return { visible: null, order: null, primary: 'pro_writer' };
  }
  const visible = new Set(ALWAYS_ON_TABS);
  for (const [tab, allowed] of Object.entries(TAB_SCOPES)) {
    if (allowed.some((s) => list.includes(s))) visible.add(tab);
  }
  // First scope in the operator's list that has a defined order wins.
  const primary = list.find((s) => SCOPE_TAB_ORDER[s]) || null;
  const order = primary
    ? SCOPE_TAB_ORDER[primary].filter((t) => visible.has(t))
    : Array.from(visible);
  return { visible, order, primary };
}
